import React, { useState, useRef } from 'react';
import { CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const TesseraRequestForm = () => {
  // 1. Inizializziamo il tempo di inizio sessione al montaggio del componente
  const formStartTime = useRef(Math.floor(Date.now() / 1000));

  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    note: '',
    website_url_check: '' // Campo Honeypot
  });
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.nome.trim()) return 'Il nome è obbligatorio';
    if (!formData.cognome.trim()) return 'Il cognome è obbligatorio';
    if (!formData.email.trim()) return 'L\'email è obbligatoria';
    if (!formData.telefono.trim()) return 'Il telefono è obbligatorio';
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) return 'Inserisci un\'email valida';
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 2. Controllo Honeypot: Se il campo nascosto è pieno, è un bot.
    // Ritorniamo "finto successo" o semplicemente ci fermiamo per non sprecare risorse API.
    if (formData.website_url_check) {
      console.warn("Spam bot detected via honeypot.");
      return; 
    }

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    // 3. Preparazione Payload con Time Trap CORRETTO
    // Usiamo formStartTime.current (tempo di caricamento pagina)
    const payload = {
        ...formData,
        form_start_time: formStartTime.current 
    };

    try {
      await axios.post(`${API_URL}/requests/tessera`, payload);
      setSubmitted(true);
      
      // Pulizia form
      setFormData({
        nome: '',
        cognome: '',
        email: '',
        telefono: '',
        note: '',
        website_url_check: ''
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Si è verificato un errore. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
      setSubmitted(false);
      // 4. Se l'utente vuole inviare un'altra richiesta, resettiamo il timer
      formStartTime.current = Math.floor(Date.now() / 1000);
  };

  if (submitted) {
    return (
      <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-teal-50">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Richiesta Inviata!
            </h3>
            <p className="text-gray-700 max-w-md mx-auto mb-6">
              Grazie! La tua richiesta è stata inviata correttamente. Ti contatteremo al più presto per l'attivazione della tua tessera VeCo.
            </p>
            <Button
              onClick={handleReset}
              variant="outline"
              className="border-green-300 text-green-700 hover:bg-green-50"
            >
              Invia un'altra richiesta
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-teal-200 hover:shadow-xl transition-shadow">
      <CardHeader className="bg-gradient-to-r from-teal-50 to-purple-50">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-purple-600 rounded-full flex items-center justify-center">
            <CreditCard className="w-6 h-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-2xl">RICHIEDI LA TUA TESSERA VECO</CardTitle>
          </div>
        </div>
        <CardDescription className="text-base">
          Compila il modulo per entrare nel circuito VeCo e iniziare a ricevere vantaggi e sconti esclusivi.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nome e Cognome */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => handleChange('nome', e.target.value)}
                placeholder="Mario"
                className="border-gray-300 focus:border-teal-500"
                required
              />
            </div>
            <div>
              <Label htmlFor="cognome">Cognome *</Label>
              <Input
                id="cognome"
                value={formData.cognome}
                onChange={(e) => handleChange('cognome', e.target.value)}
                placeholder="Rossi"
                className="border-gray-300 focus:border-teal-500"
                required
              />
            </div>
          </div>

          {/* Email e Telefono */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="mario.rossi@example.com"
                className="border-gray-300 focus:border-teal-500"
                required
              />
            </div>
            <div>
              <Label htmlFor="telefono">Telefono *</Label>
              <Input
                id="telefono"
                type="tel"
                value={formData.telefono}
                onChange={(e) => handleChange('telefono', e.target.value)}
                placeholder="+39 333 1234567"
                className="border-gray-300 focus:border-teal-500"
                required
              />
            </div>
          </div>

          {/* Note */}
          <div>
            <Label htmlFor="note">Note (facoltativo)</Label>
            <Textarea
              id="note"
              value={formData.note}
              onChange={(e) => handleChange('note', e.target.value)}
              placeholder="Eventuali note o richieste particolari..."
              rows={3}
              className="border-gray-300 focus:border-teal-500"
            />
          </div>

          {/* --- ANTISPAM FIELDS (Nascosti) --- */}
          <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
              <label htmlFor="website_url_check">Non compilare questo campo se sei umano:</label>
              <input 
                  type="text" 
                  id="website_url_check"
                  name="website_url_check" 
                  value={formData.website_url_check || ''} 
                  onChange={(e) => handleChange('website_url_check', e.target.value)} 
                  tabIndex="-1" 
                  autoComplete="off"
              />
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center space-x-2 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-teal-600 to-purple-600 hover:from-teal-700 hover:to-purple-700 text-lg py-6"
            disabled={loading}
          >
            {loading ? 'Invio in corso...' : 'Richiedi Tessera'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};